import { FinancialData } from '@api/api/earnings';
import { Icon } from '@design/components/icon/Icon';
import {
  KeyStatItem,
  KeyStats,
  ValueSegment,
} from '@design/components/key-stats/KeyStats';
import keyStatsStyles from '@design/components/key-stats/KeyStats.module.scss';
import { WithNAFallback } from '@design/components/table';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { getCurrencySymbol } from '@helpers/utils/currency/currency';
import { formatPercentage } from '@helpers/utils/numbers/numbers';
import { useGetEarningById } from '@terminal/react-query-hooks/earnings/useGetEarningById';
import { useReportedCompaniesGuidance } from '@terminal/views/theme-overview/tab-content/theme-earnings/reported-companies/useReportedCompaniesGuidance';
import {
  formatCurrencyValue,
  formatSurpriseValue,
} from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EarningsKeyStats.module.scss';

export interface EarningsKeyStatsProps {
  earningsId: string | undefined;
  entityTag?: string;
  variant: 'earnings_preview' | 'earnings_recap';
}

const buildSegments = (
  data: FinancialData,
  currencySymbol: string,
  variant: EarningsKeyStatsProps['variant']
): ValueSegment[] => {
  if (variant === 'earnings_preview') {
    return [{ text: formatCurrencyValue(data.estimate, currencySymbol) }];
  }

  const segments: ValueSegment[] = [
    { text: formatCurrencyValue(data.value, currencySymbol) },
  ];

  if (data.surprise === undefined) {
    return segments;
  }

  const surpriseText = formatSurpriseValue(data.surprise, currencySymbol);
  const percentageText =
    data.surprise_percentage !== undefined
      ? ` (${formatPercentage(data.surprise_percentage, {
          suffix: '%',
          addPlus: true,
          useOriginalValue: true,
        })})`
      : '';

  segments.push({
    text: `${surpriseText}${percentageText}`,
    colorVariant: data.surprise > 0 ? 'bullish' : 'bearish',
  });

  return segments;
};

export const EarningsKeyStats = ({
  earningsId,
  entityTag,
  variant,
}: EarningsKeyStatsProps) => {
  const { t } = useTranslation(['earnings']);
  const { data: earningsData } = useGetEarningById(earningsId);
  const isRecap = variant === 'earnings_recap';
  const guidanceRows = useMemo(
    () =>
      isRecap && earningsData && entityTag
        ? [{ ...earningsData, entity_tag: entityTag }]
        : [],
    [earningsData, entityTag, isRecap]
  );
  const guidanceByEarningsId = useReportedCompaniesGuidance(guidanceRows);

  if (!earningsData) {
    return null;
  }

  const { eps, revenue, currency } = earningsData;
  const currencySymbol = getCurrencySymbol(currency);

  const epsLabel = t(
    'earnings:earningsInsight.drawer.earningsRecap.tables.eps'
  );
  const revenueLabel = t(
    'earnings:earningsInsight.drawer.earningsRecap.tables.revenue'
  );
  const estimateLabel = t(
    'earnings:earningsInsight.drawer.earningsPreview.tables.estimate'
  ).toLowerCase();
  const epsItemLabel =
    variant === 'earnings_preview'
      ? `${epsLabel} ${estimateLabel}`
      : t('earnings:earningsInsight.drawer.earningsRecap.tables.eps');
  const revenueItemLabel =
    variant === 'earnings_preview'
      ? `${revenueLabel} ${estimateLabel}`
      : revenueLabel;

  const guidance = guidanceByEarningsId.get(earningsData.id);
  const isGuidanceRaised = guidance === 'raised';

  const items: KeyStatItem[] = [
    {
      label: revenueItemLabel,
      value: buildSegments(revenue, currencySymbol, variant),
    },
    {
      label: epsItemLabel,
      value: buildSegments(eps, currencySymbol, variant),
    },
  ];

  const stats = <KeyStats items={items} itemsPerRow={2} />;

  if (!isRecap) {
    return stats;
  }

  return (
    <div className={styles.recapStats}>
      {stats}
      <div className={keyStatsStyles.tileCard}>
        <span className={keyStatsStyles.tileLabel}>
          {t('earnings:earningsInsight.drawer.earningsRecap.tables.guidance')}
        </span>
        <span className={keyStatsStyles.tileValue}>
          {guidance ? (
            <span
              className={clsx(styles.guidance, {
                [semanticColorStyles.directionPositive]: isGuidanceRaised,
                [semanticColorStyles.directionNegative]: !isGuidanceRaised,
              })}
            >
              <Icon
                aria-hidden
                iconName={isGuidanceRaised ? 'GuidanceUp' : 'GuidanceDown'}
                size={12}
                fillColor={
                  isGuidanceRaised ? 'var(--viz-bullish)' : 'var(--viz-bearish)'
                }
              />
              {t(
                isGuidanceRaised
                  ? 'earnings:earningsInsight.drawer.earningsRecap.tables.guidanceRaised'
                  : 'earnings:earningsInsight.drawer.earningsRecap.tables.guidanceLowered'
              )}
            </span>
          ) : (
            <WithNAFallback check={false} value={null} />
          )}
        </span>
      </div>
    </div>
  );
};
